import {useTranslation} from 'react-i18next';

import {
    MIN_DAYS_AFTER_SENT,
    formatGuestCategoryList,
    readDaysAfterSent,
    readGuestCategoryFilter,
    readSurveyFilter,
    writeSurveyTriggerConfig
} from '../../../config/surveyTriggers';
import NodeEditorLayout from '../NodeEditorLayout';
import NodeNumberField from '../fields/NodeNumberField';
import {NodeEditorV2Props} from '../types';
import SurveyTriggerFilters from './SurveyTriggerFilters';

const SurveyReminderDueEditorV2 = ({
    nodeConfig,
    setNodeConfig,
    availableSurveys = [],
    availableSurveysLoading,
    readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const daysAfterSent = readDaysAfterSent(nodeConfig);
    const surveyFilter = readSurveyFilter(nodeConfig);
    const guestCategoryFilter = readGuestCategoryFilter(nodeConfig);
    // The catalogue wins over the stored snapshot: a renamed survey should read by its current name.
    const surveyName = surveyFilter.ids
        .map(id => availableSurveys.find(survey => survey.id === id)?.name || surveyFilter.names[id])
        .filter(Boolean)
        .join(', ');

    // No "Any survey": the sweep reminds an invitation once across all flows, so a catch-all steals others'.
    const noSurvey = !surveyFilter.ids.length;

    const summary = () => {
        if (noSurvey) return t('Pick at least one survey, otherwise this trigger never fires.');

        const survey = surveyName || t('a survey');

        const head = t('Runs once for every guest who was invited to {{survey}} and has not answered {{days}} days later.', {
            survey,
            days: daysAfterSent
        });

        const guestClause = guestCategoryFilter.ids.length
            ? t('Only these guests are reminded: {{guests}}.', {
                guests: formatGuestCategoryList(guestCategoryFilter, t)
            })
            : '';

        const once = t('A guest is reminded at most once.');
        const tail = t('The survey link and the guest are available to the steps below.');

        return [head, guestClause, once, tail].filter(Boolean).join(' ');
    };

    return (
        <NodeEditorLayout state={noSurvey ? 'wontRun' : 'configured'} summary={summary()}>
            <NodeNumberField
                label={t('Days after the invitation was sent')}
                value={nodeConfig.daysAfterSent ?? undefined}
                min={MIN_DAYS_AFTER_SENT}
                onChange={value => setNodeConfig(prev => writeSurveyTriggerConfig(prev, {daysAfterSent: value}))}
                disabled={readOnly}
            />

            <SurveyTriggerFilters
                nodeConfig={nodeConfig}
                setNodeConfig={setNodeConfig}
                availableSurveys={availableSurveys}
                availableSurveysLoading={availableSurveysLoading}
                excludeAnonymous
                readOnly={readOnly}
            />
        </NodeEditorLayout>
    );
};

export default SurveyReminderDueEditorV2;
